/**
 * Optimistic Updater Service
 * 
 * Handles optimistic UI updates for drawing designation changes.
 * Updates tree nodes with new designation values before server confirmation.
 */

import { DESIGNATION_PLACEHOLDER } from '../constants'
import { updateDesignationInText } from './textParser'

/**
 * Content item view node structure
 */
export interface ContentItemViewNode {
    id?: string
    dtvItemId?: string
    elements?: Array<{
        id?: string
        text?: string
        [key: string]: unknown
    }>
    [key: string]: unknown
}

/**
 * Creates an optimistic update for a drawing designation change.
 * 
 * This function updates the text within the matching content item element
 * by replacing the designation value in parentheses.
 * 
 * @param nodes - Array of content item view nodes
 * @param targetContentItemId - ID of the content item to update
 * @param newDesignation - New designation value (empty string uses placeholder)
 * @returns Updated nodes array with optimistic changes
 */
export const createOptimisticDesignationUpdate = (
    nodes: ContentItemViewNode[],
    targetContentItemId: string,
    newDesignation: string
): ContentItemViewNode[] => {
    // Use placeholder if empty
    const valueToUse = newDesignation.trim() || DESIGNATION_PLACEHOLDER

    return nodes.map((node) => {
        // Check if this node contains the target element
        const hasTargetElement = node.elements?.some(
            (element) => element.id === targetContentItemId
        )

        if (!hasTargetElement) {
            return node
        }

        // Update the matching element's text
        const updatedElements = node.elements!.map((element) => {
            if (element.id !== targetContentItemId) {
                return element
            }

            const oldText = element.text || ''
            
            // Update designation in the text
            const newText = updateDesignationInText(oldText, valueToUse)

            return {
                ...element,
                text: newText,
            }
        })

        return {
            ...node,
            elements: updatedElements,
        }
    })
}
